import { Avatar, Button, Stack, Typography } from '@mui/material';
import { useUpdatePersonalDetailsDictionary } from 'Hooks/useUpdatePersonalDetailsDictionary';
import { AccordionSection } from '../AccordionSection';
import { ChildForm } from './ChildForm';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useAppSelector, useIsDesktop } from 'Hooks';
import { IFamilyMember } from 'Interfaces/updatePersonalDetails';
import { useState } from 'react';
import { ChildCard } from './ChildCard';
import { ExistingChildCard } from './ExistingChildCard';
import { RELATION_STATUS } from 'Enum';
import { ResetButton } from '../ResetButton';
import { useParams } from 'react-router-dom';
import { PurplePlus } from 'Images';

export const AddChild = () => {
  const [editChildIndex, setEditChildIndex] = useState<number | undefined>(
    undefined
  );
  const {
    reset,
    formState: { defaultValues, errors, dirtyFields },
  } = useFormContext();
  const dictionary = useUpdatePersonalDetailsDictionary().addChild;

  const isDesktop = useIsDesktop();

  const { fields, append, remove } = useFieldArray({
    name: 'familyMembers',
  });

  const generalOptions = useAppSelector(
    (generalOptions) => generalOptions.generalOptions.generalOptions
  );

  const childInitialData: IFamilyMember = {
    identityNumber: undefined,
    firstName: undefined,
    lastName: undefined,
    birthDate: null,
    gender: generalOptions?.general?.genders?.[0]?.id || 0,
  };

  const isAddChildeDisabled = () => {
    return !(
      dirtyFields?.familyMembers &&
      Object.values({
        ...dirtyFields.familyMembers[
          editChildIndex || dirtyFields.familyMembers.length - 1
        ],
        gender: undefined,
      })?.includes(true) &&
      dirtyFields.familyMembers[
        editChildIndex || dirtyFields.familyMembers.length - 1
      ].identityNumber &&
      (errors.familyMembers === undefined ||
        Object.values(
          (errors as any).familyMembers[
            editChildIndex || dirtyFields.familyMembers.length - 1
          ]
        )?.length === 0)
    );
  };

  const addChild = () => {
    append({ ...childInitialData });
    setEditChildIndex(fields.length);
  };

  const deleteChild = (index: number) => {
    setEditChildIndex(undefined);
    remove(index);
  };

  const personalDetails = useAppSelector(
    ({ personalDetails: { personalDetails } }) => personalDetails
  );

  const params = useParams();

  const onSetEditChildIndex = (index: number) => {
    if (editChildIndex === fields.length - 1 && index !== editChildIndex) {
      deleteChild(editChildIndex);
      setEditChildIndex(index);
    }
  };

  return (
    <AccordionSection
      defaultExpanded={params.sectionId === '4'}
      title={dictionary.title}
      subtitle={dictionary.subTitle}
    >
      <Stack
        alignItems='center'
        justifyContent='center'
        role='group'
        aria-labelledby={dictionary.childList}
      >
        <Stack maxWidth={isDesktop ? '400px' : 'initial'} gap={'20px'}>
          {personalDetails?.familyMembers
            ?.filter(
              (familyMember) => familyMember.relation === RELATION_STATUS.child
            )
            .map((child, index) => (
              <ExistingChildCard
                child={{
                  isBoy: child.gender === generalOptions?.general.genders[0].id,
                  firsName: child.firstName,
                  lastName: child.lastName,
                  idNumber: child.idNumber,
                  birthDate: child.birthDate,
                }}
                childNumber={index}
                key={`${child.idNumber}-${index}`}
              />
            ))}
          {fields.map((child, index) =>
            fields.length === 1 ||
            (index === fields.length - 1 && editChildIndex === undefined) ||
            index === editChildIndex ? (
              <ChildForm key={child.id} index={index} />
            ) : (
              <ChildCard
                key={child.id}
                index={index}
                onDelete={() => deleteChild(index)}
                onEdit={() => onSetEditChildIndex(index)}
              />
            )
          )}
          <Button
            fullWidth
            variant='outlined'
            sx={{
              height: '64px',
              padding: '16px',
              borderWidth: '1px',
              borderStyle: 'dashed',
              borderColor: 'primary.shade_200',
              bgcolor: '#fff',
              borderRadius: '8px',
              gap: '12px',
              justifyContent: 'flex-start',
              '&:hover': {
                bgcolor: 'transparent',
                border: '1px dashed',
                borderColor: 'primary.main',
              },
              '&:disabled': {
                color: 'text.primary',
                borderStyle: 'dashed',
              },
            }}
            onClick={addChild}
            disabled={isAddChildeDisabled()}
            aria-disabled={isAddChildeDisabled()}
          >
            <Avatar src={PurplePlus} sx={{ width: '30px', height: '30px' }} />
            <Typography variant={'body6'}>
              {dictionary.addAnotherChild}
            </Typography>
          </Button>
          <ResetButton
            onClick={() => reset(defaultValues)}
            resetCategoryAriaText={dictionary.reset}
          />
        </Stack>
      </Stack>
    </AccordionSection>
  );
};
